import { useState } from "react";
import { site } from "../data/site";
import PageHero from "../components/PageHero";
import Button from "../components/Button";
import {
  IconArrow,
  IconMail,
  IconPhone,
  IconPin,
  IconExternal,
} from "../components/icons";
import { CompassRose, ScaleBar } from "../components/Glyphs";
import useReveal from "../components/useReveal";
import styles from "./Contact.module.css";

// No-backend contact: build a `mailto:` link from the form values and
// open the user's default email client. Works on every device, no
// third-party signup required. If the friend later wants something
// fancier, the form values are already structured — swap the submit
// handler for a fetch() to Formspree / Netlify Forms / Web3Forms /
// whatever, no markup change needed.
const PROJECT_TYPES = [
  "New Home",
  "ADU / Guesthouse",
  "Remodel / Addition",
  "Commercial",
  "Not sure yet",
];

const BUDGETS = [
  "Under $500K",
  "$500K – $1M",
  "$1M – $2M",
  "$2M+",
  "Prefer not to say",
];

const TIMELINES = [
  "Ready to start",
  "Within 6 months",
  "6–12 months out",
  "Just exploring",
];

export default function Contact() {
  useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    type: PROJECT_TYPES[0],
    budget: BUDGETS[1],
    timeline: TIMELINES[1],
    message: "",
  });
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `New project inquiry — ${form.name || "Website"}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone || "—"}`,
      `Project location: ${form.location || "—"}`,
      `Project type: ${form.type}`,
      `Budget range: ${form.budget}`,
      `Timeline: ${form.timeline}`,
      "",
      "Tell us about the project:",
      form.message || "—",
      "",
      "—",
      "Sent from upshotarch.com contact form",
    ].join("\n");
    const url = `mailto:${site.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    setSent(true);
  };

  return (
    <>
      <PageHero
        eyebrow="Get in Touch"
        code="A · 04"
        title="Tell us about your project."
        subtitle="The fastest way to start something is a phone call. If you'd rather write, the form below routes straight to Sten's inbox via your default email client — or grab the address and write us directly."
      />

      <section className={styles.contactSection}>
        <div className={styles.inner}>
          <aside className={styles.aside}>
            <div className={[styles.asideBlock, "reveal"].join(" ")}>
              <span className={styles.eyebrow}>Direct Lines</span>
              <ul className={styles.directList}>
                <li>
                  <a href={`mailto:${site.email}`} className={styles.directRow}>
                    <span className={styles.directIcon}><IconMail /></span>
                    <span className={styles.directCol}>
                      <span className={styles.directLabel}>Email</span>
                      <span className={styles.directVal}>{site.email}</span>
                    </span>
                    <IconExternal className={styles.directOut} />
                  </a>
                </li>
                <li>
                  <a href={`tel:${site.phoneTel}`} className={styles.directRow}>
                    <span className={styles.directIcon}><IconPhone /></span>
                    <span className={styles.directCol}>
                      <span className={styles.directLabel}>Phone</span>
                      <span className={styles.directVal}>{site.phone}</span>
                    </span>
                    <IconExternal className={styles.directOut} />
                  </a>
                </li>
                <li>
                  <span className={styles.directRow}>
                    <span className={styles.directIcon}><IconPin /></span>
                    <span className={styles.directCol}>
                      <span className={styles.directLabel}>Studio</span>
                      <span className={styles.directVal}>{site.city}</span>
                    </span>
                  </span>
                </li>
              </ul>
            </div>

            <div className={[styles.asideBlock, styles.asideDark, "reveal"].join(" ")}>
              <CompassRose size={56} color="#d8c9a8" className={styles.asideCompass} />
              <span className={styles.asideEyebrow}>Service Area</span>
              <p className={styles.asideBody}>
                The studio is based in Bozeman and works across Southwest
                Montana — Gallatin Valley, Paradise Valley, Big Sky, and the
                Bridger and Madison ranges. Outside the region? Let's still
                talk.
              </p>
              <ScaleBar width={200} color="#d8c9a8" label="UPSHOT · BZN · A4" />
            </div>
          </aside>

          <form className={[styles.form, "reveal"].join(" ")} onSubmit={handleSubmit}>
            <div className={styles.formHead}>
              <span className={styles.eyebrow}>Project Inquiry</span>
              <h2 className={styles.formTitle}>Tell us a little about it.</h2>
            </div>

            <div className={styles.row2}>
              <Field
                label="Name"
                code="01"
                value={form.name}
                onChange={update("name")}
                required
                placeholder="Jane Architect"
              />
              <Field
                label="Email"
                code="02"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                placeholder="you@email.com"
              />
            </div>
            <div className={styles.row2}>
              <Field
                label="Phone"
                code="03"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="(406) 000-0000"
              />
              <Field
                label="Project location"
                code="04"
                value={form.location}
                onChange={update("location")}
                placeholder="Bozeman, MT"
              />
            </div>
            <div className={styles.row3}>
              <Select
                label="Project type"
                code="05"
                value={form.type}
                onChange={update("type")}
                options={PROJECT_TYPES}
              />
              <Select
                label="Budget"
                code="06"
                value={form.budget}
                onChange={update("budget")}
                options={BUDGETS}
              />
              <Select
                label="Timeline"
                code="07"
                value={form.timeline}
                onChange={update("timeline")}
                options={TIMELINES}
              />
            </div>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>
                <span className={styles.fieldCode}>08</span>
                Tell us about it
              </span>
              <textarea
                rows={6}
                className={styles.textarea}
                value={form.message}
                onChange={update("message")}
                placeholder="Site, program, what you've been imagining. As much or as little as you want."
              />
            </label>

            <div className={styles.formFoot}>
              <Button type="submit" variant="primary" size="lg">
                Send Inquiry <IconArrow />
              </Button>
              <span className={styles.formNote}>
                {sent
                  ? "Your email client should have opened. If not, write us directly at "
                  : "Submitting opens your email client with a draft to "}
                <a href={`mailto:${site.email}`}>{site.email}</a>.
              </span>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, code, ...rest }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        <span className={styles.fieldCode}>{code}</span>
        {label}
      </span>
      <input className={styles.input} {...rest} />
    </label>
  );
}

function Select({ label, code, value, onChange, options }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        <span className={styles.fieldCode}>{code}</span>
        {label}
      </span>
      <select className={styles.input} value={value} onChange={onChange}>
        {options.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}
